const FONT = '14px "Segoe UI", system-ui, sans-serif';
const MAX_TEXT_W = 150;
const PAD_X = 16;
const PAD_Y = 10;
const LINE_H = 18;
const SIBLING_GAP = 36;
const LEVEL_GAP = 78;
const BUS_PAD = 18;
const CANVAS_PAD = 64;

function bendOffset(node, axis) {
  const value = Number(axis === "x" ? node?.bendX : node?.bendY);
  return Number.isFinite(value) ? value : 0;
}

function placedBend(start, end, offset) {
  const mid = (start + end) / 2 + offset;
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  if (hi - lo < 36) return (start + end) / 2;
  return Math.min(hi - 16, Math.max(lo + 16, mid));
}

let measureCanvas;

function textWidth(text, font = FONT) {
  const value = String(text || "");
  if (typeof document === "undefined") return value.length * 7.4;
  if (!measureCanvas) measureCanvas = document.createElement("canvas");
  const ctx = measureCanvas.getContext("2d");
  if (!ctx) return value.length * 7.4;
  ctx.font = font || FONT;
  return ctx.measureText(value).width;
}

function wrapText(text, maxWidth) {
  const raw = String(text || "").trim();
  if (!raw) return [];
  const words = raw.split(/\s+/);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const chunks = [];
    if (textWidth(word) > maxWidth) {
      let buf = "";
      for (const ch of word) {
        if (buf && textWidth(buf + ch) > maxWidth) {
          chunks.push(buf);
          buf = ch;
        } else {
          buf += ch;
        }
      }
      if (buf) chunks.push(buf);
    } else {
      chunks.push(word);
    }
    chunks.forEach((chunk) => {
      const next = line ? `${line} ${chunk}` : chunk;
      if (line && textWidth(next) > maxWidth) {
        lines.push(line);
        line = chunk;
      } else {
        line = next;
      }
    });
  });
  if (line) lines.push(line);
  return lines.slice(0, 6);
}

function contentMetrics(text) {
  const raw = String(text || "").trim();
  const lines = raw ? wrapText(raw, MAX_TEXT_W) : [];
  const sample = lines.length ? lines : ["Text"];
  return {
    lines,
    contentW: Math.max(...sample.map((line) => textWidth(line))) + 4,
    contentH: sample.length * LINE_H,
  };
}

function textFrame(width, height, contentW, contentH) {
  return {
    x: (width - contentW) / 2,
    y: (height - contentH) / 2,
    w: contentW,
    h: contentH,
  };
}

function sizeNode(node) {
  const { lines, contentW, contentH } = contentMetrics(node.text);
  const shape = node.shape || "rounded";
  let w;
  let h;
  let textBox;
  let skew;

  if (shape === "circle") {
    const diameter = Math.max(36, Math.ceil(Math.hypot(contentW + 16, contentH + 16)));
    w = diameter;
    h = diameter;
    textBox = textFrame(w, h, contentW, contentH);
  } else if (shape === "rhombus") {
    const across = contentW / 2 + 8;
    const down = contentH / 2 + 8;
    w = Math.ceil((2 * across) / 0.55);
    h = Math.ceil((2 * down) / 0.45);
    textBox = textFrame(w, h, contentW, contentH);
  } else if (shape === "triangle") {
    const pad = 12;
    h = Math.ceil(3 * (contentH / 2 + pad));
    const textTop = (2 / 3) * h - contentH / 2;
    w = Math.ceil((contentW + pad) / (textTop / h));
    textBox = { x: (w - contentW) / 2, y: textTop, w: contentW, h: contentH };
  } else if (shape === "rhomboid") {
    h = contentH + PAD_Y * 2;
    skew = Math.max(16, Math.round(h * 0.28));
    const inner = contentW + 8;
    w = inner + skew * 2;
    textBox = {
      x: skew + (inner - contentW) / 2,
      y: (h - contentH) / 2,
      w: contentW,
      h: contentH,
    };
  } else {
    w = contentW + PAD_X * 2;
    h = contentH + PAD_Y * 2;
    textBox = textFrame(w, h, contentW, contentH);
  }

  return { ...node, w, h, lines, textBox, skew };
}

function buildTree(nodes) {
  const sized = nodes.map(sizeNode);
  const byId = new Map(sized.map((node) => [node.id, { ...node, children: [] }]));
  const rootSource = sized.find((node) => !node.free && (!node.parentId || !byId.has(node.parentId)))
    || sized.find((node) => !node.free)
    || sized[0];
  if (!rootSource) return null;
  const root = byId.get(rootSource.id);
  const seen = new Set([root.id]);
  const attach = (node) => {
    const kids = [];
    sized.forEach((candidate, index) => {
      if (candidate.free || candidate.parentId !== node.id || seen.has(candidate.id)) return;
      kids.push({ candidate, index });
    });
    kids.sort((a, b) => {
      const ao = Number(a.candidate.order);
      const bo = Number(b.candidate.order);
      const av = Number.isFinite(ao) ? ao : a.index;
      const bv = Number.isFinite(bo) ? bo : b.index;
      return av - bv || a.index - b.index;
    });
    kids.forEach(({ candidate }) => {
      const child = byId.get(candidate.id);
      seen.add(child.id);
      node.children.push(child);
      attach(child);
    });
  };
  attach(root);
  return root;
}

function packCenters(kids, getBefore, getAfter) {
  if (!kids.length) return [];
  const befores = kids.map(getBefore);
  const afters = kids.map(getAfter);
  const centers = [0];
  for (let i = 1; i < kids.length; i += 1) {
    centers.push(centers[i - 1] + afters[i - 1] + SIBLING_GAP + befores[i]);
  }
  const count = kids.length;
  const shift = count % 2 === 1
    ? -centers[(count - 1) >> 1]
    : -((centers[0] - befores[0]) + (centers[count - 1] + afters[count - 1])) / 2;
  return centers.map((center) => center + shift);
}

function subtreeRects(node, originX, originY, list) {
  list.push({
    x: originX + node.lx,
    y: originY + node.ly,
    w: node.w,
    h: node.h,
  });
  node.children.forEach((child) => {
    subtreeRects(child, originX + child.ox, originY + child.oy, list);
  });
  return list;
}

function rangesOverlap(start, end, otherStart, otherEnd, gap) {
  return start < otherEnd + gap && end + gap > otherStart;
}

function applySpan(child) {
  child.tempX += Number(child.spanX) || 0;
  child.tempY += Number(child.spanY) || 0;
}

function sideReach(child, vertical, sign) {
  if (vertical) return sign < 0 ? child.subW - child.lx - child.w : child.lx;
  return sign < 0 ? child.subH - child.ly - child.h : child.ly;
}

function sideClearance(rects, obstacles, vertical, sign) {
  let push = 0;
  rects.forEach((rect) => {
    obstacles.forEach((block) => {
      const cross = vertical
        ? rangesOverlap(rect.y, rect.y + rect.h, block.y, block.y + block.h, LEVEL_GAP)
        : rangesOverlap(rect.x, rect.x + rect.w, block.x, block.x + block.w, LEVEL_GAP);
      if (!cross) return;
      const along = vertical
        ? rangesOverlap(rect.x, rect.x + rect.w, block.x, block.x + block.w, LEVEL_GAP)
        : rangesOverlap(rect.y, rect.y + rect.h, block.y, block.y + block.h, LEVEL_GAP);
      if (!along) return;
      const need = vertical
        ? (sign > 0
          ? block.x + block.w + LEVEL_GAP - rect.x
          : rect.x + rect.w + LEVEL_GAP - block.x)
        : (sign > 0
          ? block.y + block.h + LEVEL_GAP - rect.y
          : rect.y + rect.h + LEVEL_GAP - block.y);
      if (need > push) push = need;
    });
  });
  return push;
}

function layoutOrthogonal(root, mode) {
  const vertical = mode !== "leftRight";
  const beforeSide = vertical ? "left" : "up";
  const afterSide = vertical ? "right" : "down";

  const measure = (node) => {
    node.children.forEach(measure);
    const before = [];
    const after = [];
    const main = [];
    node.children.forEach((child) => {
      child.attach = attachOf(child);
      if (child.attach === beforeSide) before.push(child);
      else if (child.attach === afterSide) after.push(child);
      else main.push(child);
    });

    const parentMain = vertical ? node.h : node.w;
    const parentCross = vertical ? node.w : node.h;
    const mainCenters = packCenters(
      main,
      (child) => (vertical ? child.lx + child.w / 2 : child.ly + child.h / 2),
      (child) => (vertical ? child.subW - child.lx - child.w / 2 : child.subH - child.ly - child.h / 2)
    );
    main.forEach((child, index) => {
      if (vertical) {
        const nodeX = parentCross / 2 + mainCenters[index] - child.w / 2;
        child.tempX = nodeX - child.lx;
        child.tempY = parentMain + LEVEL_GAP;
      } else {
        const nodeY = parentCross / 2 + mainCenters[index] - child.h / 2;
        child.tempX = parentMain + LEVEL_GAP;
        child.tempY = nodeY - child.ly;
      }
      applySpan(child);
    });

    const obstacles = [{ x: 0, y: 0, w: node.w, h: node.h }];
    main.forEach((child) => subtreeRects(child, child.tempX, child.tempY, obstacles));

    const placeSide = (kids, sign) => {
      if (!kids.length) return;
      const centers = packCenters(
        kids,
        (child) => (vertical ? child.ly + child.h / 2 : child.lx + child.w / 2),
        (child) => (vertical ? child.subH - child.ly - child.h / 2 : child.subW - child.lx - child.w / 2)
      );
      const reach = kids.reduce((max, child) => Math.max(max, sideReach(child, vertical, sign)), 0);
      const extra = Math.max(0, reach * 2 + BUS_PAD * 2 - LEVEL_GAP);
      kids.forEach((child, index) => {
        const along = parentMain / 2 + centers[index];
        if (vertical) {
          child.tempY = along - child.h / 2 - child.ly;
          child.tempX = sign < 0
            ? -LEVEL_GAP - extra - child.w - child.lx
            : parentCross + LEVEL_GAP + extra - child.lx;
        } else {
          child.tempX = along - child.w / 2 - child.lx;
          child.tempY = sign < 0
            ? -LEVEL_GAP - extra - child.h - child.ly
            : parentCross + LEVEL_GAP + extra - child.ly;
        }
      });
      for (let guard = 0; guard < 16; guard += 1) {
        const rects = [];
        kids.forEach((child) => subtreeRects(child, child.tempX, child.tempY, rects));
        const push = sideClearance(rects, obstacles, vertical, sign);
        if (push <= 0.5) break;
        kids.forEach((child) => {
          if (vertical) child.tempX += sign * push;
          else child.tempY += sign * push;
        });
      }
      kids.forEach((child) => {
        applySpan(child);
        subtreeRects(child, child.tempX, child.tempY, obstacles);
      });
    };
    placeSide(before, -1);
    placeSide(after, 1);

    let minX = 0;
    let minY = 0;
    let maxX = node.w;
    let maxY = node.h;
    node.children.forEach((child) => {
      minX = Math.min(minX, child.tempX);
      minY = Math.min(minY, child.tempY);
      maxX = Math.max(maxX, child.tempX + child.subW);
      maxY = Math.max(maxY, child.tempY + child.subH);
    });
    node.lx = -minX;
    node.ly = -minY;
    node.subW = maxX - minX;
    node.subH = maxY - minY;
    node.children.forEach((child) => {
      child.ox = child.tempX - minX;
      child.oy = child.tempY - minY;
    });
  };

  const assign = (node, x, y) => {
    node.x = x + node.lx;
    node.y = y + node.ly;
    node.children.forEach((child) => assign(child, x + child.ox, y + child.oy));
  };

  measure(root);
  assign(root, 0, 0);
}

function layoutTopDown(root) {
  layoutOrthogonal(root, "topDown");
}

function layoutLeftRight(root) {
  layoutOrthogonal(root, "leftRight");
}

function shiftSubtree(node, dx, dy) {
  node.x += dx;
  node.y += dy;
  node.children.forEach((child) => shiftSubtree(child, dx, dy));
}

function nudgeSubtree(node, id, dx, dy) {
  if (node.id === id) {
    if (node.parentId) shiftSubtree(node, dx, dy);
    return true;
  }
  return node.children.some((child) => nudgeSubtree(child, id, dx, dy));
}

function placeOnRing(node, angle, radius) {
  node.angle = angle;
  node.x = Math.cos(angle) * radius - node.w / 2;
  node.y = Math.sin(angle) * radius - node.h / 2;
}

function layoutRadial(root) {
  root.x = -root.w / 2;
  root.y = -root.h / 2;
  root.angle = -Math.PI / 2;
  const placeChildren = (parent, angle, radius, sector) => {
    const kids = parent.children;
    if (!kids.length) return;
    const count = kids.length;
    const minStep = 0.62;
    const span = parent === root ? Math.PI * 2 : Math.max(sector * 0.94, count * minStep);
    const start = parent === root ? -Math.PI / 2 : angle - span / 2;
    const step = span / count;
    const maxChild = Math.max(...kids.map((child) => Math.max(child.w, child.h)));
    const nextRadius = radius + maxChild * 0.72 + 84;
    kids.forEach((child, index) => {
      const childAngle = start + step * (index + 0.5);
      placeOnRing(child, childAngle, nextRadius);
      placeChildren(child, childAngle, nextRadius, step);
    });
  };
  placeChildren(root, -Math.PI / 2, 0, Math.PI * 2);
}

function flatten(node, list = []) {
  list.push(node);
  node.children.forEach((child) => flatten(child, list));
  return list;
}

function centerOf(node) {
  return { x: node.x + node.w / 2, y: node.y + node.h / 2 };
}

function borderPoint(node, toward) {
  const c = centerOf(node);
  const dx = toward.x - c.x;
  const dy = toward.y - c.y;
  if (!dx && !dy) return { ...c };
  const scale = Math.min(
    dx === 0 ? Infinity : (node.w / 2) / Math.abs(dx),
    dy === 0 ? Infinity : (node.h / 2) / Math.abs(dy)
  );
  return { x: c.x + dx * scale, y: c.y + dy * scale };
}

function outset(point, node, distance) {
  const c = centerOf(node);
  const dx = point.x - c.x;
  const dy = point.y - c.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: point.x + (dx / len) * distance, y: point.y + (dy / len) * distance };
}

function attachOf(child) {
  if (child?.branch === "left" || child?.branch === "right" || child?.branch === "up" || child?.branch === "down") {
    return child.branch;
  }
  return null;
}

function sideBranch(layout, branch) {
  if (layout === "leftRight") return branch === "up" || branch === "down";
  if (layout === "topDown") return branch === "left" || branch === "right";
  return false;
}

function sidePoints(parent, child, branch) {
  if (branch === "left" || branch === "right") {
    const y1 = parent.y + parent.h / 2;
    const y2 = child.y + child.h / 2;
    const x1 = branch === "left" ? parent.x : parent.x + parent.w;
    const x2 = branch === "left" ? child.x + child.w : child.x;
    if (Math.abs(y1 - y2) < 1.5) return [{ x: x1, y: y1 }, { x: x2, y: y2 }];
    const midX = placedBend(x1, x2, bendOffset(child, "x"));
    return [
      { x: x1, y: y1 },
      { x: midX, y: y1 },
      { x: midX, y: y2 },
      { x: x2, y: y2 },
    ];
  }
  const x1 = parent.x + parent.w / 2;
  const x2 = child.x + child.w / 2;
  const y1 = branch === "up" ? parent.y : parent.y + parent.h;
  const y2 = branch === "up" ? child.y + child.h : child.y;
  if (Math.abs(x1 - x2) < 1.5) return [{ x: x1, y: y1 }, { x: x2, y: y2 }];
  const midY = placedBend(y1, y2, bendOffset(child, "y"));
  return [
    { x: x1, y: y1 },
    { x: x1, y: midY },
    { x: x2, y: midY },
    { x: x2, y: y2 },
  ];
}

function treePoints(layout, parent, child) {
  const branch = child.attach || child.branch;
  if (sideBranch(layout, branch)) return sidePoints(parent, child, branch);
  if (layout === "leftRight") {
    const y1 = parent.y + parent.h / 2;
    const y2 = child.y + child.h / 2;
    const x1 = parent.x + parent.w;
    const x2 = child.x;
    const midX = placedBend(x1, x2, bendOffset(child, "x"));
    return [
      { x: x1, y: y1 },
      { x: midX, y: y1 },
      { x: midX, y: y2 },
      { x: x2, y: y2 },
    ];
  }
  if (layout === "topDown") {
    const x1 = parent.x + parent.w / 2;
    const x2 = child.x + child.w / 2;
    const y1 = parent.y + parent.h;
    const y2 = child.y;
    const midY = placedBend(y1, y2, bendOffset(child, "y"));
    return [
      { x: x1, y: y1 },
      { x: x1, y: midY },
      { x: x2, y: midY },
      { x: x2, y: y2 },
    ];
  }
  const start = borderPoint(parent, centerOf(child));
  const end = borderPoint(child, centerOf(parent));
  return [start, end];
}

function pathLength(points) {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return total;
}

export function polylineMidpoint(points) {
  if (!points?.length) return { x: 0, y: 0 };
  const total = pathLength(points);
  let remain = total / 2;
  for (let i = 1; i < points.length; i += 1) {
    const len = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    if (len >= remain) {
      const t = len === 0 ? 0 : remain / len;
      return {
        x: points[i - 1].x + (points[i].x - points[i - 1].x) * t,
        y: points[i - 1].y + (points[i].y - points[i - 1].y) * t,
      };
    }
    remain -= len;
  }
  return points[points.length - 1];
}

function segmentHitsRect(a, b, rect, pad) {
  const left = rect.x - pad;
  const top = rect.y - pad;
  const right = rect.x + rect.w + pad;
  const bottom = rect.y + rect.h + pad;
  const steps = Math.max(4, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / 8));
  for (let i = 1; i < steps; i += 1) {
    const t = i / steps;
    const x = a.x + (b.x - a.x) * t;
    const y = a.y + (b.y - a.y) * t;
    if (x > left && x < right && y > top && y < bottom) return true;
  }
  return false;
}

function pathHits(points, obstacles) {
  for (let i = 1; i < points.length; i += 1) {
    if (obstacles.some((rect) => segmentHitsRect(points[i - 1], points[i], rect, 8))) return true;
  }
  return false;
}

function simplifyPath(points) {
  const out = [];
  points.forEach((point) => {
    const prev = out[out.length - 1];
    if (prev && Math.abs(prev.x - point.x) < 0.8 && Math.abs(prev.y - point.y) < 0.8) return;
    const earlier = out[out.length - 2];
    if (earlier && prev) {
      const vertical = Math.abs(earlier.x - prev.x) < 0.8 && Math.abs(prev.x - point.x) < 0.8;
      const horizontal = Math.abs(earlier.y - prev.y) < 0.8 && Math.abs(prev.y - point.y) < 0.8;
      if (vertical || horizontal) {
        out[out.length - 1] = point;
        return;
      }
    }
    out.push(point);
  });
  return out;
}

function heapPush(heap, item) {
  heap.push(item);
  let i = heap.length - 1;
  while (i > 0) {
    const parent = (i - 1) >> 1;
    if (heap[parent].f <= heap[i].f) break;
    [heap[parent], heap[i]] = [heap[i], heap[parent]];
    i = parent;
  }
}

function heapPop(heap) {
  const top = heap[0];
  const last = heap.pop();
  if (heap.length && last) {
    heap[0] = last;
    let i = 0;
    for (;;) {
      const left = i * 2 + 1;
      const right = left + 1;
      let smallest = i;
      if (left < heap.length && heap[left].f < heap[smallest].f) smallest = left;
      if (right < heap.length && heap[right].f < heap[smallest].f) smallest = right;
      if (smallest === i) break;
      [heap[smallest], heap[i]] = [heap[i], heap[smallest]];
      i = smallest;
    }
  }
  return top;
}

function routeAroundBox(start, end, obstacles) {
  let minX = Math.min(start.x, end.x);
  let minY = Math.min(start.y, end.y);
  let maxX = Math.max(start.x, end.x);
  let maxY = Math.max(start.y, end.y);
  obstacles.forEach((rect) => {
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.w);
    maxY = Math.max(maxY, rect.y + rect.h);
  });
  const margin = 26;
  const candidates = [
    [start, { x: start.x, y: minY - margin }, { x: end.x, y: minY - margin }, end],
    [start, { x: start.x, y: maxY + margin }, { x: end.x, y: maxY + margin }, end],
    [start, { x: minX - margin, y: start.y }, { x: minX - margin, y: end.y }, end],
    [start, { x: maxX + margin, y: start.y }, { x: maxX + margin, y: end.y }, end],
  ];
  const clear = candidates.find((path) => !pathHits(path, obstacles));
  if (clear) return clear;
  return candidates.slice().sort((a, b) => pathLength(a) - pathLength(b))[0];
}

function routeAvoiding(start, end, obstacles) {
  const elbowH = [start, { x: end.x, y: start.y }, end];
  const elbowV = [start, { x: start.x, y: end.y }, end];
  if (!pathHits(elbowH, obstacles)) return elbowH;
  if (!pathHits(elbowV, obstacles)) return elbowV;

  const cell = 18;
  let minX = Math.min(start.x, end.x);
  let minY = Math.min(start.y, end.y);
  let maxX = Math.max(start.x, end.x);
  let maxY = Math.max(start.y, end.y);
  obstacles.forEach((rect) => {
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.w);
    maxY = Math.max(maxY, rect.y + rect.h);
  });
  minX -= 56;
  minY -= 56;
  maxX += 56;
  maxY += 56;
  const cols = Math.ceil((maxX - minX) / cell) + 1;
  const rows = Math.ceil((maxY - minY) / cell) + 1;
  if (cols < 2 || rows < 2 || cols * rows > 12000) return routeAroundBox(start, end, obstacles);

  const toCell = (point) => ({
    c: Math.max(0, Math.min(cols - 1, Math.round((point.x - minX) / cell))),
    r: Math.max(0, Math.min(rows - 1, Math.round((point.y - minY) / cell))),
  });
  const toPoint = (c, r) => ({ x: minX + c * cell, y: minY + r * cell });
  const index = (c, r) => r * cols + c;
  const blocked = new Uint8Array(cols * rows);
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const point = toPoint(c, r);
      if (obstacles.some((rect) => point.x > rect.x - 10 && point.x < rect.x + rect.w + 10 && point.y > rect.y - 10 && point.y < rect.y + rect.h + 10)) {
        blocked[index(c, r)] = 1;
      }
    }
  }
  const startCell = toCell(start);
  const endCell = toCell(end);
  blocked[index(startCell.c, startCell.r)] = 0;
  blocked[index(endCell.c, endCell.r)] = 0;

  const key = (c, r) => `${c},${r}`;
  const open = [];
  heapPush(open, { c: startCell.c, r: startCell.r, g: 0, f: 0 });
  const best = new Map([[key(startCell.c, startCell.r), 0]]);
  const came = new Map();
  const closed = new Set();
  const heuristic = (c, r) => Math.abs(c - endCell.c) + Math.abs(r - endCell.r);

  while (open.length) {
    const current = heapPop(open);
    const currentKey = key(current.c, current.r);
    if (closed.has(currentKey)) continue;
    closed.add(currentKey);
    if (current.c === endCell.c && current.r === endCell.r) {
      const cells = [{ c: current.c, r: current.r }];
      let walk = currentKey;
      while (came.has(walk)) {
        const prev = came.get(walk);
        cells.push(prev);
        walk = key(prev.c, prev.r);
      }
      cells.reverse();
      return simplifyPath([start, ...cells.map((item) => toPoint(item.c, item.r)), end]);
    }
    [[1, 0], [-1, 0], [0, 1], [0, -1]].forEach(([dc, dr]) => {
      const nc = current.c + dc;
      const nr = current.r + dr;
      if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) return;
      if (blocked[index(nc, nr)]) return;
      const nextKey = key(nc, nr);
      if (closed.has(nextKey)) return;
      const nextG = current.g + 1;
      if (nextG >= (best.get(nextKey) ?? Infinity)) return;
      best.set(nextKey, nextG);
      came.set(nextKey, { c: current.c, r: current.r });
      heapPush(open, { c: nc, r: nr, g: nextG, f: nextG + heuristic(nc, nr) });
    });
  }
  return routeAroundBox(start, end, obstacles);
}

const LANE_GAP = 40;

function sidePorts(node) {
  const center = centerOf(node);
  return [
    { side: "top", x: center.x, y: node.y },
    { side: "right", x: node.x + node.w, y: center.y },
    { side: "bottom", x: center.x, y: node.y + node.h },
    { side: "left", x: node.x, y: center.y },
  ];
}

function outerLanes(nodes, gap) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  nodes.forEach((node) => {
    minX = Math.min(minX, node.x);
    minY = Math.min(minY, node.y);
    maxX = Math.max(maxX, node.x + node.w);
    maxY = Math.max(maxY, node.y + node.h);
  });
  return { left: minX - gap, top: minY - gap, right: maxX + gap, bottom: maxY + gap };
}

function lanePoint(port, lanes) {
  if (port.side === "top") return { x: port.x, y: lanes.top };
  if (port.side === "bottom") return { x: port.x, y: lanes.bottom };
  if (port.side === "left") return { x: lanes.left, y: port.y };
  return { x: lanes.right, y: port.y };
}

function laneEdge(point, lanes) {
  const eps = 1.2;
  if (Math.abs(point.y - lanes.top) <= eps) return "top";
  if (Math.abs(point.x - lanes.right) <= eps) return "right";
  if (Math.abs(point.y - lanes.bottom) <= eps) return "bottom";
  if (Math.abs(point.x - lanes.left) <= eps) return "left";
  return null;
}

const NEXT_LANE_CORNER = {
  top: (lanes) => ({ x: lanes.right, y: lanes.top, edge: "right" }),
  right: (lanes) => ({ x: lanes.right, y: lanes.bottom, edge: "bottom" }),
  bottom: (lanes) => ({ x: lanes.left, y: lanes.bottom, edge: "left" }),
  left: (lanes) => ({ x: lanes.left, y: lanes.top, edge: "top" }),
};

function walkLanes(start, end, lanes) {
  const startEdge = laneEdge(start, lanes);
  const endEdge = laneEdge(end, lanes);
  if (!startEdge || !endEdge || startEdge === endEdge) return [start, end];
  const points = [start];
  let edge = startEdge;
  for (let guard = 0; guard < 4 && edge !== endEdge; guard += 1) {
    const corner = NEXT_LANE_CORNER[edge](lanes);
    points.push({ x: corner.x, y: corner.y });
    edge = corner.edge;
  }
  points.push(end);
  return points;
}

function shorterLanePath(start, end, lanes) {
  const clockwise = walkLanes(start, end, lanes);
  const counter = walkLanes(end, start, lanes).slice().reverse();
  return pathLength(clockwise) <= pathLength(counter) ? clockwise : counter;
}

function nearestSide(node, point) {
  const center = centerOf(node);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? "right" : "left";
  return dy > 0 ? "bottom" : "top";
}

function occupiedSides(node, nodes, layout) {
  const occupied = new Set();
  const parent = nodes.find((other) => other.id === node.parentId);
  const children = nodes.filter((other) => other.parentId === node.id);
  if (layout === "radial") {
    if (parent) occupied.add(nearestSide(node, centerOf(parent)));
    if (children.length) {
      const average = children.reduce((acc, child) => {
        const center = centerOf(child);
        acc.x += center.x;
        acc.y += center.y;
        return acc;
      }, { x: 0, y: 0 });
      average.x /= children.length;
      average.y /= children.length;
      occupied.add(nearestSide(node, average));
    }
    return occupied;
  }
  if (parent) {
    if (layout === "leftRight" && node.branch === "up") occupied.add("bottom");
    else if (layout === "leftRight" && node.branch === "down") occupied.add("top");
    else if (layout !== "leftRight" && node.branch === "left") occupied.add("right");
    else if (layout !== "leftRight" && node.branch === "right") occupied.add("left");
    else occupied.add(layout === "leftRight" ? "left" : "top");
  }
  children.forEach((child) => {
    const branch = child.attach || child.branch;
    if (layout === "leftRight" && branch === "up") occupied.add("top");
    else if (layout === "leftRight" && branch === "down") occupied.add("bottom");
    else if (layout !== "leftRight" && branch === "left") occupied.add("left");
    else if (layout !== "leftRight" && branch === "right") occupied.add("right");
    else occupied.add(layout === "leftRight" ? "right" : "bottom");
  });
  return occupied;
}

function openPorts(node, nodes, layout) {
  const occupied = occupiedSides(node, nodes, layout);
  const open = sidePorts(node).filter((port) => !occupied.has(port.side));
  return open.length ? open : sidePorts(node);
}

function routeAroundDiagram(from, to, nodes, gap, layout) {
  const obstacles = nodes.filter((node) => node.id !== from.id && node.id !== to.id);
  const lanes = outerLanes(nodes, gap);
  let best = null;
  let bestLen = Infinity;
  openPorts(from, nodes, layout).forEach((fromPort) => {
    openPorts(to, nodes, layout).forEach((toPort) => {
      const out = lanePoint(fromPort, lanes);
      const into = lanePoint(toPort, lanes);
      if (segmentHitsRect(fromPort, out, from, 0)) return;
      if (obstacles.some((node) => segmentHitsRect(fromPort, out, node, 6))) return;
      if (obstacles.some((node) => segmentHitsRect(toPort, into, node, 6))) return;
      const around = shorterLanePath(out, into, lanes);
      if (obstacles.some((node) => pathHits(around, [node]))) return;
      const points = simplifyPath([fromPort, ...around, toPort]);
      const len = pathLength(points);
      if (len < bestLen) {
        bestLen = len;
        best = points;
      }
    });
  });
  return best;
}

function portPoint(node, side, at) {
  const t = Math.min(1, Math.max(0, Number.isFinite(at) ? at : 0.5));
  if (side === "top") return { x: node.x + node.w * t, y: node.y };
  if (side === "bottom") return { x: node.x + node.w * t, y: node.y + node.h };
  if (side === "left") return { x: node.x, y: node.y + node.h * t };
  return { x: node.x + node.w, y: node.y + node.h * t };
}

function verticalSide(side) {
  return side === "top" || side === "bottom";
}

function finiteOr(value, fallback) {
  if (value == null || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function outsideX(node, side, bend, gap) {
  if (side === "left") return Math.min(finiteOr(bend, node.x - gap), node.x - gap);
  return Math.max(finiteOr(bend, node.x + node.w + gap), node.x + node.w + gap);
}

function manualLinkPoints(from, to, route) {
  const a = portPoint(from, route.fromSide, route.fromAt);
  const b = portPoint(to, route.toSide, route.toAt);
  const aVert = verticalSide(route.fromSide);
  const bVert = verticalSide(route.toSide);
  const gap = 28;
  if (!aVert && !bVert) {
    const x1 = outsideX(from, route.fromSide, route.fromBend, gap);
    const x2 = outsideX(to, route.toSide, route.toBend, gap);
    const floor = Math.max(from.y + from.h, to.y + to.h) + gap;
    const lane = finiteOr(route.lane, floor);
    return simplifyPath([
      a,
      { x: x1, y: a.y },
      { x: x1, y: lane },
      { x: x2, y: lane },
      { x: x2, y: b.y },
      b,
    ]);
  }
  const sharedH = finiteOr(route.h, aVert && bVert ? (a.y + b.y) / 2 : (aVert ? a.y : b.y));
  const sharedV = finiteOr(route.v, aVert ? b.x : a.x);
  const fromBend = finiteOr(route.fromBend, aVert ? sharedH : sharedV);
  const toBend = finiteOr(route.toBend, bVert ? sharedH : sharedV);
  const a2 = aVert ? { x: a.x, y: fromBend } : { x: fromBend, y: a.y };
  const b2 = bVert ? { x: b.x, y: toBend } : { x: toBend, y: b.y };
  const corner = aVert && bVert
    ? { x: b.x, y: fromBend }
    : aVert
      ? { x: b2.x, y: a2.y }
      : { x: a2.x, y: b2.y };
  return simplifyPath([a, a2, corner, b2, b]);
}

function linkPoints(from, to, nodes, laneIndex, layout, route) {
  if (route?.fromSide && route?.toSide) {
    const manual = manualLinkPoints(from, to, route);
    if (manual.length >= 2) return manual;
  }
  const gap = LANE_GAP + Math.max(0, laneIndex) * 18;
  const routed = routeAroundDiagram(from, to, nodes, gap, layout);
  if (routed) return routed;
  const obstacles = nodes.filter((node) => node.id !== from.id && node.id !== to.id);
  const startBorder = borderPoint(from, centerOf(to));
  const endBorder = borderPoint(to, centerOf(from));
  const start = outset(startBorder, from, 8);
  const end = outset(endBorder, to, 8);
  return simplifyPath([startBorder, ...routeAvoiding(start, end, obstacles), endBorder]);
}

function plusPoint(layout, node) {
  const gap = 14;
  if (node.out === "left") return { x: node.x - gap, y: node.y + node.h / 2 };
  if (node.out === "right") return { x: node.x + node.w + gap, y: node.y + node.h / 2 };
  if (node.out === "up") return { x: node.x + node.w / 2, y: node.y - gap };
  if (node.out === "down") return { x: node.x + node.w / 2, y: node.y + node.h + gap };
  if (layout === "leftRight") return { x: node.x + node.w + gap, y: node.y + node.h / 2 };
  if (layout === "radial" && node.parentId) {
    const len = Math.max(node.w, node.h) / 2 + gap;
    return {
      x: node.x + node.w / 2 + Math.cos(node.angle || 0) * len,
      y: node.y + node.h / 2 + Math.sin(node.angle || 0) * len,
    };
  }
  if (layout === "radial") return { x: node.x + node.w + gap, y: node.y + node.h / 2 };
  return { x: node.x + node.w / 2, y: node.y + node.h + gap };
}

function shiftDiagram(nodes, edges, links, lock, extras = []) {
  let minX = 0;
  let minY = 0;
  let maxX = 240;
  let maxY = 160;
  const consider = (point) => {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  };
  extras.forEach((box) => {
    consider({ x: box.x, y: box.y });
    consider({ x: box.x + (box.w || 0), y: box.y + (box.h || 0) });
  });
  nodes.forEach((node) => {
    consider({ x: node.x, y: node.y });
    consider({ x: node.x + node.w, y: node.y + node.h });
    consider(node.plus);
  });
  [...edges, ...links].forEach((edge) => edge.points.forEach(consider));
  const dx = lock ? lock.x : CANVAS_PAD - minX;
  const dy = lock ? lock.y : CANVAS_PAD - minY;
  const move = (point) => ({ x: point.x + dx, y: point.y + dy });
  nodes.forEach((node) => {
    node.x += dx;
    node.y += dy;
    node.plus = move(node.plus);
  });
  [...edges, ...links].forEach((edge) => {
    edge.points = edge.points.map(move);
    edge.mid = move(edge.mid);
  });
  const left = minX + dx;
  const top = minY + dy;
  const right = maxX + dx;
  const bottom = maxY + dy;
  return {
    width: Math.ceil(Math.max(right, CANVAS_PAD) - Math.min(left, 0) + CANVAS_PAD),
    height: Math.ceil(Math.max(bottom, CANVAS_PAD) - Math.min(top, 0) + CANVAS_PAD),
    shift: { x: dx, y: dy },
  };
}

function freeOwnerId(node, byId) {
  let current = node;
  const seen = new Set();
  while (current && !seen.has(current.id)) {
    if (current.free) return current.id;
    seen.add(current.id);
    if (!current.parentId) return null;
    current = byId.get(current.parentId) || null;
  }
  return null;
}

function layoutTree(root, safeLayout) {
  if (safeLayout === "radial") layoutRadial(root);
  else if (safeLayout === "leftRight") layoutLeftRight(root);
  else layoutTopDown(root);
}

function appendLaidTree(root, safeLayout, flat, treeEdges, edgesIn, anchor, nudge) {
  if (!root) return;
  layoutTree(root, safeLayout);
  if (nudge && Number.isFinite(nudge.dx) && Number.isFinite(nudge.dy)) {
    nudgeSubtree(root, nudge.id, nudge.dx, nudge.dy);
  }
  if (anchor) {
    shiftSubtree(root, (Number(anchor.x) || 0) - root.x, (Number(anchor.y) || 0) - root.y);
  }
  const walk = (node) => {
    node.children.forEach((child) => {
      const points = simplifyPath(treePoints(safeLayout, node, child));
      const style = defaultStyle(edgesIn[`${node.id}:${child.id}`]);
      treeEdges.push({
        key: `${node.id}:${child.id}`,
        fromId: node.id,
        toId: child.id,
        points,
        mid: polylineMidpoint(points),
        ...style,
      });
      walk(child);
    });
  };
  walk(root);
  flatten(root).forEach((node) => {
    flat.push({
      ...node,
      free: Boolean(anchor && node.id === anchor.id),
      plus: plusPoint(safeLayout, node),
      children: undefined,
    });
  });
}

function readNotes(raw) {
  return (Array.isArray(raw) ? raw : [])
    .filter((note) => note && typeof note === "object" && note.id)
    .slice(0, 40)
    .map((note) => {
      const sections = (Array.isArray(note.sections) ? note.sections : [])
        .filter((section) => section && typeof section === "object" && section.id)
        .slice(0, 8)
        .map((section) => ({
          id: section.id,
          header: String(section.header || ""),
          body: String(section.body || ""),
          headerFill: section.headerFill || "#6b7280",
          bodyFill: section.bodyFill || "#ffffff",
        }));
      if (!sections.length) {
        sections.push({
          id: `${note.id}s`,
          header: "Header",
          body: "",
          headerFill: "#6b7280",
          bodyFill: "#ffffff",
        });
      }
      const w = Number(note.w);
      const h = Number(note.h);
      return {
        id: note.id,
        x: Number.isFinite(Number(note.x)) ? Number(note.x) : 0,
        y: Number.isFinite(Number(note.y)) ? Number(note.y) : 0,
        w: Number.isFinite(w) ? Math.max(180, Math.min(1200, w)) : 280,
        h: Number.isFinite(h) ? Math.max(120, Math.min(1200, h)) : 220,
        flow: note.flow === "horizontal" ? "horizontal" : "vertical",
        design: ["plain", "sidebar", "outline", "note"].includes(note.design) ? note.design : "card",
        sections,
      };
    });
}

function lineColor(value) {
  const color = String(value || "").trim();
  return /^#[0-9a-fA-F]{3,8}$/.test(color) ? color : "#64748b";
}

function defaultStyle(style) {
  return {
    text: style?.text || "",
    pattern: style?.pattern || "solid",
    thickness: Number(style?.thickness) || 1.5,
    arrow: style?.arrow === "start" || style?.arrow === "end" || style?.arrow === "both" ? style.arrow : "none",
    arrowHead: ["arrow", "triangle", "diamond", "filled", "plus"].includes(style?.arrowHead) ? style.arrowHead : "arrow",
    color: lineColor(style?.color),
  };
}

export function layoutFlow(layout, document, options = {}) {
  const safeLayout = layout === "radial" || layout === "leftRight" ? layout : "topDown";
  const nodesIn = Array.isArray(document?.nodes) ? document.nodes : [];
  const nodesById = new Map(nodesIn.filter(Boolean).map((node) => [node.id, node]));
  const freeGroups = new Map();
  const mainNodes = [];
  nodesIn.forEach((node) => {
    if (!node) return;
    const ownerId = freeOwnerId(node, nodesById);
    if (!ownerId) {
      mainNodes.push(node);
      return;
    }
    if (!freeGroups.has(ownerId)) freeGroups.set(ownerId, []);
    freeGroups.get(ownerId).push(node);
  });
  const root = mainNodes.length ? buildTree(mainNodes) : null;
  const flat = [];
  const treeEdges = [];
  const edgesIn = document?.edges || {};
  const nudge = options.nudge;
  appendLaidTree(root, safeLayout, flat, treeEdges, edgesIn, null, nudge);
  freeGroups.forEach((members) => {
    const anchor = members.find((node) => node.free) || members[0];
    const prepared = members.map((node) => ({
      ...node,
      free: false,
      parentId: node.id === anchor.id ? null : node.parentId,
    }));
    appendLaidTree(buildTree(prepared), safeLayout, flat, treeEdges, edgesIn, anchor, nudge);
  });
  const byId = new Map(flat.map((node) => [node.id, node]));

  const links = (Array.isArray(document?.links) ? document.links : [])
    .filter((link) => byId.has(link.fromId) && byId.has(link.toId) && link.fromId !== link.toId)
    .map((link, index) => {
      const points = linkPoints(byId.get(link.fromId), byId.get(link.toId), flat, index, safeLayout, link.route);
      const style = defaultStyle(link);
      return {
        ...link,
        points,
        mid: polylineMidpoint(points),
        ...style,
      };
    });

  const rawLabels = (Array.isArray(document?.labels) ? document.labels : [])
    .filter((label) => label && typeof label === "object")
    .map((label) => ({
      id: label.id,
      text: String(label.text || ""),
      x: Number.isFinite(Number(label.x)) ? Number(label.x) : 0,
      y: Number.isFinite(Number(label.y)) ? Number(label.y) : 0,
    }));
  const labelBoxes = rawLabels.map((label) => ({
    x: label.x,
    y: label.y,
    w: Math.max(80, Math.min(260, (label.text || "Label").length * 7.4 + 52)),
    h: 28,
  }));
  const rawNotes = readNotes(document?.notes);
  const noteBoxes = rawNotes.map((note) => ({ x: note.x, y: note.y, w: note.w, h: note.h }));
  const bounds = shiftDiagram(flat, treeEdges, links, options.shiftLock, [...labelBoxes, ...noteBoxes]);
  const labels = rawLabels.map((label) => ({
    ...label,
    x: label.x + bounds.shift.x,
    y: label.y + bounds.shift.y,
  }));
  const notes = rawNotes.map((note) => ({
    ...note,
    x: note.x + bounds.shift.x,
    y: note.y + bounds.shift.y,
  }));
  return { ...bounds, nodes: flat, treeEdges, links, labels, notes };
}

function segmentHit(x, y, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((x - a.x) * dx + (y - a.y) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const px = a.x + dx * t;
  const py = a.y + dy * t;
  return { dist: Math.hypot(x - px, y - py), t };
}

export function nodeSide(node, point) {
  const cx = node.x + node.w / 2;
  const cy = node.y + node.h / 2;
  const nx = (point.x - cx) / (node.w / 2 || 1);
  const ny = (point.y - cy) / (node.h / 2 || 1);
  if (Math.abs(nx) > Math.abs(ny)) return nx >= 0 ? "right" : "left";
  return ny >= 0 ? "bottom" : "top";
}

function exitSide(node, point) {
  if (point.x <= node.x) return "left";
  if (point.x >= node.x + node.w) return "right";
  const mid = node.x + node.w / 2;
  if (point.x < mid - node.w * 0.18) return "left";
  if (point.x > mid + node.w * 0.18) return "right";
  return "bottom";
}

function pointAlong(node, side, point) {
  const raw = side === "top" || side === "bottom"
    ? (point.x - node.x) / (node.w || 1)
    : (point.y - node.y) / (node.h || 1);
  return Math.min(1, Math.max(0, raw));
}

export function linkDragTarget(points, x, y) {
  if (!points || points.length < 2) return "h";
  const startDist = Math.hypot(x - points[0].x, y - points[0].y);
  const endDist = Math.hypot(x - points[points.length - 1].x, y - points[points.length - 1].y);
  if (startDist <= 64 && startDist <= endDist) return "from";
  if (endDist <= 64) return "to";
  if (points.length === 2) return startDist <= endDist ? "from" : "to";
  let best = { dist: Infinity, index: 0, horiz: true };
  for (let i = 1; i < points.length; i += 1) {
    const hit = segmentHit(x, y, points[i - 1], points[i]);
    if (hit.dist < best.dist) {
      best = {
        dist: hit.dist,
        index: i - 1,
        horiz: Math.abs(points[i].x - points[i - 1].x) >= Math.abs(points[i].y - points[i - 1].y),
      };
    }
  }
  if (best.index === 0) return "from";
  if (best.index === points.length - 2) return "to";
  return best.horiz ? "h" : "v";
}

export function seedLinkRoute(fromNode, toNode, points, shift) {
  if (!points || points.length < 2) {
    return {
      fromSide: "bottom",
      toSide: "bottom",
      fromAt: 0.5,
      toAt: 0.5,
      fromBend: null,
      toBend: null,
    };
  }
  const sx = shift?.x || 0;
  const sy = shift?.y || 0;
  const fromSide = nodeSide(fromNode, points[0]);
  const toSide = nodeSide(toNode, points[points.length - 1]);
  const fromElbow = points[1];
  const toElbow = points[points.length - 2];
  const fromBend = verticalSide(fromSide) ? fromElbow.y - sy : fromElbow.x - sx;
  const toBend = verticalSide(toSide) ? toElbow.y - sy : toElbow.x - sx;
  return {
    fromSide,
    toSide,
    fromAt: pointAlong(fromNode, fromSide, points[0]),
    toAt: pointAlong(toNode, toSide, points[points.length - 1]),
    fromBend,
    toBend,
  };
}

function bendFallback(route, side, bendKey) {
  if (Number.isFinite(route[bendKey])) return route[bendKey];
  if (verticalSide(side)) return Number.isFinite(route.h) ? route.h : null;
  return Number.isFinite(route.v) ? route.v : null;
}

export function nextLinkRoute({ fromNode, toNode, route, part, x, y, shift }) {
  const preX = x - (shift?.x || 0);
  const preY = y - (shift?.y || 0);
  const next = {
    fromSide: route.fromSide,
    toSide: route.toSide,
    fromAt: Number.isFinite(route.fromAt) ? route.fromAt : 0.5,
    toAt: Number.isFinite(route.toAt) ? route.toAt : 0.5,
    fromBend: bendFallback(route, route.fromSide, "fromBend"),
    toBend: bendFallback(route, route.toSide, "toBend"),
    lane: Number.isFinite(route.lane) ? route.lane : null,
  };
  const adjustEnd = (node, sideKey, atKey, bendKey) => {
    const side = exitSide(node, { x, y });
    const sx = shift?.x || 0;
    const sy = shift?.y || 0;
    const localX = x - sx;
    const localY = y - sy;
    next[sideKey] = side;
    next[atKey] = 0.5;
    if (side === "bottom") {
      next[bendKey] = Math.max(localY, node.y - sy + node.h + 16);
      return;
    }
    if (side === "left") next[bendKey] = Math.min(localX, node.x - sx - 16);
    else next[bendKey] = Math.max(localX, node.x + node.w - sx + 16);
    const top = node.y - sy;
    const bottom = node.y + node.h - sy;
    if (localY < top - 8 || localY > bottom + 8) next.lane = localY;
  };
  if (part === "from") {
    adjustEnd(fromNode, "fromSide", "fromAt", "fromBend");
    return next;
  }
  if (part === "to") {
    adjustEnd(toNode, "toSide", "toAt", "toBend");
    return next;
  }
  if (part === "v") {
    if (!verticalSide(next.fromSide)) next.fromBend = preX;
    if (!verticalSide(next.toSide)) next.toBend = preX;
    return next;
  }
  if (!verticalSide(next.fromSide) && !verticalSide(next.toSide)) {
    next.lane = preY;
    return next;
  }
  if (verticalSide(next.fromSide)) next.fromBend = preY;
  if (verticalSide(next.toSide)) next.toBend = preY;
  return next;
}

export { textWidth };
